import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star, Clock, CalendarOff } from "lucide-react";

const WEEKDAYS = [
  { v: 0, l: "Seg" }, { v: 1, l: "Ter" }, { v: 2, l: "Qua" },
  { v: 3, l: "Qui" }, { v: 4, l: "Sex" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

const DEFAULT_INTERVALS = [
  { start: "08:00", end: "12:00", weekdays: [0, 1, 2, 3, 4] },
  { start: "14:00", end: "17:00", weekdays: [0, 1, 2, 3, 4] },
];

// Convert either the new shape ({intervals}) or legacy ({start,end,weekdays})
// into the array UI always renders.
const toIntervalsList = (wh) => {
  if (!wh) return [...DEFAULT_INTERVALS];
  if (Array.isArray(wh.intervals) && wh.intervals.length > 0) {
    return wh.intervals.map((i) => ({
      start: i.start || "08:00",
      end: i.end || "12:00",
      weekdays: Array.isArray(i.weekdays) ? [...i.weekdays] : [0, 1, 2, 3, 4],
    }));
  }
  if (wh.start && wh.end) {
    return [{ start: wh.start, end: wh.end, weekdays: [...(wh.weekdays || [0, 1, 2, 3, 4])] }];
  }
  return [...DEFAULT_INTERVALS];
};

const empty = { name: "", specialty: "", service_ids: [], rating: 5.0, photo_url: "",
                working_hours: { intervals: [...DEFAULT_INTERVALS] },
                buffer_minutes: 60, active: true };

// ── Blocks (folgas / feriados) subpanel ──
function BlocksPanel({ professionalId }) {
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ start_at: "", end_at: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const today = new Date().toISOString();
      const { data } = await api.get(`/admin/professionals/${professionalId}/blocks?from_date=${today}`);
      setBlocks(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { if (professionalId) load(); /* eslint-disable-next-line */ }, [professionalId]);

  const add = async () => {
    if (!form.start_at || !form.end_at) { toast.error("Informe início e fim."); return; }
    const start = new Date(form.start_at);
    const end = new Date(form.end_at);
    if (end <= start) { toast.error("Fim deve ser posterior ao início."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/professionals/${professionalId}/blocks`, {
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        reason: form.reason,
      });
      toast.success("Bloqueio adicionado.");
      setForm({ start_at: "", end_at: "", reason: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao adicionar."); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remover bloqueio "${b.reason || 'sem descrição'}"?`)) return;
    try {
      await api.delete(`/admin/professionals/${professionalId}/blocks/${b.id}`);
      toast.success("Bloqueio removido.");
      await load();
    } catch (_) { toast.error("Erro ao remover."); }
  };

  return (
    <div className="border-t border-border pt-4 space-y-4" data-testid="blocks-panel">
      <div className="overline text-gold inline-flex items-center gap-2"><CalendarOff className="w-3.5 h-3.5" /> Bloqueios (folgas · feriados · ausências)</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Início</label>
          <input type="datetime-local" value={form.start_at}
            onChange={(e) => setForm({ ...form, start_at: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="block-start-input" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Fim</label>
          <input type="datetime-local" value={form.end_at}
            onChange={(e) => setForm({ ...form, end_at: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="block-end-input" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Motivo (opcional)</label>
          <input value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Ex: Feriado, congresso, folga"
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="block-reason-input" />
        </div>
      </div>
      <button onClick={add} disabled={saving}
        className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        data-testid="add-block-btn">
        <Plus className="w-4 h-4" /> Adicionar bloqueio
      </button>

      {blocks.length > 0 && (
        <ul className="space-y-2">
          {blocks.map((b) => (
            <li key={b.id} className="flex items-center justify-between text-sm bg-muted/40 border border-border rounded-sm px-3 py-2" data-testid={`block-item-${b.id}`}>
              <div>
                <div className="font-medium">
                  {new Date(b.start_at).toLocaleString("pt-BR")} → {new Date(b.end_at).toLocaleString("pt-BR")}
                </div>
                {b.reason && <div className="text-xs text-muted-foreground">{b.reason}</div>}
              </div>
              <button onClick={() => remove(b)} className="p-1.5 rounded-sm hover:bg-destructive/10 transition-colors" data-testid={`remove-block-${b.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {blocks.length === 0 && (
        <div className="text-xs text-muted-foreground">Nenhum bloqueio futuro cadastrado.</div>
      )}
    </div>
  );
}

export default function AdminProfessionalsPage() {
  const [pros, setPros] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/admin/professionals"), api.get("/admin/services")]);
      setPros(p.data);
      setServices(s.data.filter((x) => x.active));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty, working_hours: { intervals: toIntervalsList(null) } });
  const openEdit = (p) => setEditing({
    ...p,
    working_hours: { intervals: toIntervalsList(p.working_hours) },
    service_ids: p.service_ids || [],
  });

  const toggleService = (id) => {
    setEditing((prev) => {
      const has = prev.service_ids.includes(id);
      return { ...prev, service_ids: has ? prev.service_ids.filter((x) => x !== id) : [...prev.service_ids, id] };
    });
  };
  const updateInterval = (idx, patch) => {
    setEditing((prev) => {
      const list = [...(prev.working_hours.intervals || [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, working_hours: { intervals: list } };
    });
  };
  const toggleIntervalWeekday = (idx, v) => {
    setEditing((prev) => {
      const list = [...(prev.working_hours.intervals || [])];
      const it = list[idx];
      const has = it.weekdays.includes(v);
      const next = has ? it.weekdays.filter((x) => x !== v) : [...it.weekdays, v].sort();
      list[idx] = { ...it, weekdays: next };
      return { ...prev, working_hours: { intervals: list } };
    });
  };
  const addInterval = () => {
    setEditing((prev) => ({
      ...prev,
      working_hours: {
        intervals: [...(prev.working_hours.intervals || []), { start: "14:00", end: "17:00", weekdays: [0, 1, 2, 3, 4] }],
      },
    }));
  };
  const removeInterval = (idx) => {
    setEditing((prev) => {
      const list = [...(prev.working_hours.intervals || [])];
      list.splice(idx, 1);
      return { ...prev, working_hours: { intervals: list.length > 0 ? list : [...DEFAULT_INTERVALS] } };
    });
  };

  const validateIntervals = (intervals) => {
    for (const [i, it] of intervals.entries()) {
      if (!/^\d{2}:\d{2}$/.test(it.start) || !/^\d{2}:\d{2}$/.test(it.end)) return `Intervalo ${i + 1}: horário inválido.`;
      if (it.end <= it.start) return `Intervalo ${i + 1}: fim deve ser após o início.`;
      if (!it.weekdays || it.weekdays.length === 0) return `Intervalo ${i + 1}: selecione ao menos um dia da semana.`;
    }
    return null;
  };

  const save = async () => {
    if (!editing.name || !editing.specialty) {
      toast.error("Preencha nome e especialidade.");
      return;
    }
    const intervals = editing.working_hours?.intervals || [];
    if (intervals.length === 0) {
      toast.error("Adicione ao menos um intervalo de expediente.");
      return;
    }
    const err = validateIntervals(intervals);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name, specialty: editing.specialty,
        service_ids: editing.service_ids, rating: Number(editing.rating),
        photo_url: editing.photo_url, working_hours: { intervals },
        buffer_minutes: Number(editing.buffer_minutes ?? 60),
      };
      if (editing.id) {
        payload.active = editing.active;
        await api.patch(`/admin/professionals/${editing.id}`, payload);
        toast.success("Profissional atualizado.");
      } else {
        await api.post("/admin/professionals", payload);
        toast.success("Profissional cadastrado.");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Desativar profissional "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/professionals/${p.id}`);
      toast.success("Profissional desativado.");
      await load();
    } catch (_) { toast.error("Erro."); }
  };

  const svcNameById = (id) => services.find((s) => s.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="admin-professionals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Equipe clínica</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Profissionais</h1>
          <p className="text-muted-foreground mt-2">Vincule serviços prestados e defina horários de trabalho.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="new-professional-btn">
          <Plus className="w-4 h-4" /> Novo profissional
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pros.map((p) => (
          <div key={p.id} className="bg-card border border-border rounded-sm p-5" data-testid={`admin-pro-${p.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground grid place-items-center font-heading font-bold flex-none">
                {(p.name.split(" ")[1] || p.name)[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.specialty}</div>
                <div className="inline-flex items-center gap-1 mt-1 text-xs text-gold">
                  <Star className="w-3 h-3 fill-current" /> {p.rating}
                </div>
              </div>
              <span className={`overline px-2 py-0.5 rounded-sm border ${p.active ? "border-success/40 text-success bg-success/5" : "border-border text-muted-foreground bg-muted"}`}>
                {p.active ? "Ativo" : "Inativo"}
              </span>
            </div>
            <div className="mt-3 space-y-2">
              <div className="text-xs text-muted-foreground inline-flex items-start gap-1">
                <Clock className="w-3 h-3 mt-0.5 flex-none" />
                <span data-testid={`pro-wh-summary-${p.id}`}>
                  {toIntervalsList(p.working_hours).map((it, i) => (
                    <span key={i} className="inline-block mr-2">
                      {it.start}–{it.end} <span className="opacity-70">({(it.weekdays || []).map((w) => WEEKDAYS[w].l).join(",")})</span>
                    </span>
                  ))}
                </span>
                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-muted flex-none">intervalo {p.buffer_minutes ?? 60} min</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(p.service_ids || []).map((sid) => (
                  <span key={sid} className="text-[10px] bg-muted px-2 py-0.5 rounded-sm">{svcNameById(sid)}</span>
                ))}
                {(!p.service_ids || p.service_ids.length === 0) && <span className="text-xs text-muted-foreground">Sem serviços vinculados</span>}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => openEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-border text-xs font-semibold py-2 hover:bg-muted transition-colors" data-testid={`edit-pro-${p.id}`}>
                <Pencil className="w-3 h-3" /> Editar
              </button>
              {p.active && (
                <button onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-sm border border-destructive/30 text-destructive text-xs font-semibold px-3 py-2 hover:bg-destructive/5 transition-colors" data-testid={`delete-pro-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {pros.length === 0 && (
          <div className="md:col-span-3 bg-card border border-border rounded-sm p-10 text-center text-muted-foreground">Nenhum profissional cadastrado.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setEditing(null)} data-testid="professional-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold mb-1">{editing.id ? "Editar profissional" : "Novo profissional"}</div>
                <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">{editing.id ? editing.name : "Cadastrar"}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded-sm" data-testid="close-pro-modal"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Nome</label>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pro-name-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Especialidade</label>
                  <input value={editing.specialty} onChange={(e) => setEditing({ ...editing, specialty: e.target.value })} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pro-specialty-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Rating</label>
                  <input type="number" step="0.1" min={0} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pro-rating-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Foto URL (opcional)</label>
                  <input value={editing.photo_url || ""} onChange={(e) => setEditing({ ...editing, photo_url: e.target.value })} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pro-photo-input" />
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground mb-2 block">Serviços prestados</label>
                <div className="flex flex-wrap gap-2">
                  {services.map((s) => {
                    const checked = editing.service_ids.includes(s.id);
                    return (
                      <button
                        key={s.id} type="button"
                        onClick={() => toggleService(s.id)}
                        className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${checked ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                        data-testid={`pro-service-toggle-${s.id}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                  {services.length === 0 && <span className="text-xs text-muted-foreground">Cadastre serviços primeiro.</span>}
                </div>
              </div>

              <div className="border border-border rounded-sm p-4 bg-muted/30 space-y-4" data-testid="pro-working-hours-editor">
                <div className="flex items-center justify-between">
                  <div className="overline text-gold">Expediente · intervalos de trabalho</div>
                  <button
                    type="button" onClick={addInterval}
                    className="inline-flex items-center gap-1 rounded-sm border border-primary/40 text-primary px-2 py-1 text-xs font-semibold hover:bg-primary/5 transition-colors"
                    data-testid="add-interval-btn"
                  >
                    <Plus className="w-3 h-3" /> Adicionar intervalo
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground -mt-2">
                  Ex.: manhã 08:00–12:00 e tarde 14:00–17:00. Cada intervalo tem seus próprios dias da semana.
                </p>
                {(editing.working_hours.intervals || []).map((it, idx) => (
                  <div key={idx} className="border border-border rounded-sm p-3 bg-card space-y-3" data-testid={`interval-row-${idx}`}>
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="overline text-muted-foreground mb-1 block">Início</label>
                        <input
                          type="time" value={it.start}
                          onChange={(e) => updateInterval(idx, { start: e.target.value })}
                          className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                          data-testid={`interval-start-${idx}`}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="overline text-muted-foreground mb-1 block">Fim</label>
                        <input
                          type="time" value={it.end}
                          onChange={(e) => updateInterval(idx, { end: e.target.value })}
                          className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                          data-testid={`interval-end-${idx}`}
                        />
                      </div>
                      <button
                        type="button" onClick={() => removeInterval(idx)}
                        className="p-2 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                        title="Remover intervalo"
                        data-testid={`remove-interval-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div>
                      <label className="overline text-muted-foreground mb-2 block">Dias da semana</label>
                      <div className="flex flex-wrap gap-2">
                        {WEEKDAYS.map((w) => {
                          const checked = (it.weekdays || []).includes(w.v);
                          return (
                            <button
                              key={w.v} type="button"
                              onClick={() => toggleIntervalWeekday(idx, w.v)}
                              className={`px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${checked ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                              data-testid={`interval-${idx}-day-${w.v}`}
                            >
                              {w.l}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {editing.id && (
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-primary w-4 h-4" data-testid="pro-active-checkbox" />
                  <span className="text-sm">Profissional ativo (visível para pacientes)</span>
                </label>
              )}

              <div>
                <label className="overline text-muted-foreground mb-1 block">Intervalo entre consultas (minutos)</label>
                <input
                  type="number" min={0} max={480} step={5}
                  value={editing.buffer_minutes ?? 60}
                  onChange={(e) => setEditing({ ...editing, buffer_minutes: Number(e.target.value) })}
                  className="w-32 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  data-testid="pro-buffer-input"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Tempo mínimo obrigatório entre o fim de uma consulta e o início da próxima. Padrão: 60 min.
                </div>
              </div>

              {editing.id && <BlocksPanel professionalId={editing.id} />}

              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-pro-btn">Cancelar</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors" data-testid="save-pro-btn">
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
